import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../shared/auth/AuthContext';
import { authApi } from '../../shared/api/auth';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  initials: z.string().max(8).optional(),
  honorificTitle: z.string().max(64).optional(),
  jobTitle: z.string().max(128).optional(),
  department: z.string().max(128).optional(),
  country: z.string().max(64).optional(),
  address: z.string().max(256).optional(),
});

type FormValues = z.infer<typeof schema>;

export default function ProfilePage(): JSX.Element {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      initials: user?.initials ?? '',
      honorificTitle: user?.honorificTitle ?? '',
      jobTitle: user?.jobTitle ?? '',
      department: user?.department ?? '',
      country: user?.country ?? '',
      address: user?.address ?? '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setSuccess(false);
    try {
      await authApi.updateMe({
        firstName: values.firstName,
        lastName: values.lastName,
        initials: values.initials || null,
        honorificTitle: values.honorificTitle || null,
        jobTitle: values.jobTitle || null,
        department: values.department || null,
        country: values.country || null,
        address: values.address || null,
      });
      const refreshed = await refreshUser();
      setSuccess(true);
      if (user?.mustCompleteProfile && refreshed && !refreshed.mustCompleteProfile) {
        navigate('/', { replace: true });
      }
    } catch {
      setServerError('Could not save your profile. Please try again.');
    }
  });

  if (!user) return <></>;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Your profile
        </Typography>
        {user.mustCompleteProfile && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Please complete your profile to continue.
          </Alert>
        )}
        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            {success && <Alert severity="success">Profile saved.</Alert>}
            <TextField label="Email" value={user.email} disabled />
            <Stack direction="row" spacing={2}>
              <TextField
                label="First name"
                fullWidth
                error={Boolean(errors.firstName)}
                helperText={errors.firstName?.message}
                {...register('firstName')}
              />
              <TextField
                label="Last name"
                fullWidth
                error={Boolean(errors.lastName)}
                helperText={errors.lastName?.message}
                {...register('lastName')}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Initials" fullWidth {...register('initials')} />
              <TextField label="Honorific title" fullWidth {...register('honorificTitle')} />
            </Stack>
            <TextField label="Job title" {...register('jobTitle')} />
            <TextField label="Department" {...register('department')} />
            <TextField label="Country" {...register('country')} />
            <TextField label="Address" multiline minRows={2} {...register('address')} />
            <Button type="submit" variant="contained" disabled={isSubmitting} size="large">
              {isSubmitting ? 'Saving…' : 'Save profile'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
